import { useForm } from "react-hook-form";
import { X, CreditCard } from "lucide-react";

import { Input } from "shared/components/inputs/Input";
import { ICartao } from "../../shared/interfaces/IMetodo";

import styles from "./CdCartao.module.scss";

import { criarCartao } from "shared/services/api/cartao.api";

import {
  maskCartao,
  maskCvv,
  maskNomeCartao,
  maskValidade,
} from "shared/utils/masks";

import { buscarUsuario } from "shared/services/api/usuario.api";
import { criarTokenCartao } from "shared/services/pagarme/pagarme.api";

interface Props {
  cartao?: ICartao | null;
  onClose: () => void;
}

export const CdCartao: React.FC<Props> = ({
  cartao,
  onClose,
}) => {

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      numero: cartao?.numero || "",
      nome: cartao?.nome || "",
      validade: cartao?.validade || "",
      cvv: cartao?.cvv || "",
      defaultCard: cartao?.defaultCard ?? false,
    },
  });

  /* =========================================
     SALVAR CARTÃO
  ========================================= */

  const onSubmit = async (data: any) => {
    try {

      const donoCartao = await buscarUsuario();

      const dadosCartao = {
        id: "",
        numero: data.numero,
        nome: data.nome,
        documento: donoCartao?.cpf,
        validade: data.validade,
        cvv: data.cvv,
        defaultCard: data.defaultCard,
      };

      const pagarmeToken =
        await criarTokenCartao(dadosCartao);

      if (!pagarmeToken) {
        throw new Error(
          "Houve um erro ao gerar o token do cartão"
        );
      }

      await criarCartao(pagarmeToken);

      onClose();

    } catch (error) {

      console.error(
        "Erro ao salvar cartão:",
        error
      );

    }
  };

  return (
    <div className={styles.page}>

      {/* =====================================
          CABEÇALHO
      ===================================== */}

      <div className={styles.header}>

        <div className={styles.headerInfo}>

          <div className={styles.headerIcon}>
            <CreditCard size={20} />
          </div>

          <div>
            <h2 className={styles.titulo}>
              Dados do cartão
            </h2>

            <p className={styles.descricao}>
              Preencha os dados do cartão para
              adicioná-lo aos seus métodos de pagamento.
            </p>
          </div>

        </div>

        <button
          type="button"
          className={styles.iconClose}
          onClick={onClose}
          aria-label="Fechar"
        >
          <X size={22} />
        </button>

      </div>


      {/* =====================================
          FORMULÁRIO
      ===================================== */}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.form}
      >

        {/* ===================================
            NÚMERO
        =================================== */}

        <div className={styles.containerLabel}>

          <label
            className={styles.label}
            htmlFor="numero"
          >
            Número do cartão
            <span>*</span>
          </label>

          <Input
            type="text"
            name="numero"
            placeholder="Digite o número do cartão"
            register={register("numero", {
              onChange: (e) => {
                e.target.value =
                  maskCartao(e.target.value);
              },
            })}
            error={
              errors.numero?.message as string
            }
            customClass={styles.customInput}
          />

        </div>


        {/* ===================================
            NOME
        =================================== */}

        <div className={styles.containerLabel}>

          <label
            className={styles.label}
            htmlFor="nome"
          >
            Nome impresso no cartão
            <span>*</span>
          </label>

          <Input
            type="text"
            name="nome"
            placeholder="Digite o nome exatamente como aparece no cartão"
            register={register("nome", {
              onChange: (e) => {
                e.target.value =
                  maskNomeCartao(
                    e.target.value
                  );
              },
            })}
            error={
              errors.nome?.message as string
            }
            customClass={styles.customInput}
          />

        </div>


        {/* ===================================
            VALIDADE + CVV
        =================================== */}

        <div className={styles.inputs2}>

          <div
            className={`${styles.containerLabel} ${styles.validade}`}
          >

            <label
              className={styles.label}
              htmlFor="validade"
            >
              Validade
              <span>*</span>
            </label>

            <Input
              type="text"
              name="validade"
              placeholder="MM/AA"
              register={register("validade", {
                onChange: (e) => {
                  e.target.value =
                    maskValidade(
                      e.target.value
                    );
                },
              })}
              error={
                errors.validade?.message as string
              }
              customClass={styles.customInput}
            />

          </div>


          <div
            className={`${styles.containerLabel} ${styles.cvv}`}
          >

            <label
              className={styles.label}
              htmlFor="cvv"
            >
              CVV
              <span>*</span>
            </label>

            <Input
              type="text"
              name="cvv"
              placeholder="123"
              register={register("cvv", {
                onChange: (e) => {
                  e.target.value =
                    maskCvv(
                      e.target.value
                    );
                },
              })}
              error={
                errors.cvv?.message as string
              }
              customClass={styles.customInput}
            />

          </div>

        </div>


        {/* ===================================
            CARTÃO PRINCIPAL
        =================================== */}

        <label
          htmlFor="defaultCard"
          className={styles.checkboxContainer}
        >

          <input
            type="checkbox"
            id="defaultCard"
            {...register("defaultCard")}
          />

          <span>
            Salvar como método principal
          </span>

        </label>


        {/* ===================================
            BOTÕES
        =================================== */}

        <div className={styles.btnContainer}>

          <button
            type="button"
            onClick={onClose}
            className={styles.btnCancelar}
          >
            Cancelar
          </button>

          <button
            type="submit"
            className={styles.btnSave}
          >
            Salvar cartão
          </button>

        </div>

      </form>

    </div>
  );
};